"use client";

import { motion } from "framer-motion";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface ProductsSkeletonGridProps {
	count?: number;
	columns?: 3 | 4;
	className?: string;
}

const container = {
	hidden: { opacity: 0 },
	show: {
		opacity: 1,
		transition: {
			staggerChildren: 0.05,
		},
	},
};

const item = {
	hidden: { opacity: 0, y: 20 },
	show: {
		opacity: 1,
		y: 0,
		transition: {
			duration: 0.3,
		},
	},
};

function ProductCardSkeleton() {
	return (
		<div className="group flex flex-col h-full bg-card text-card-foreground rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300">
			<Skeleton className="relative block aspect-4/3 w-full rounded-none bg-secondary" />

			<div className="flex flex-1 flex-col p-4">
				<Skeleton className="h-5 w-3/4 mb-2" />
				<div className="flex flex-wrap gap-1 mt-1.5">
					<Skeleton className="h-4 w-16 rounded-full" />
					<Skeleton className="h-4 w-12 rounded-full" />
				</div>
				<div className="mt-2 sm:mt-3 space-y-1.5 mb-2">
					<Skeleton className="h-3 sm:h-3.5 w-full" />
					<Skeleton className="h-3 sm:h-3.5 w-5/6" />
				</div>

				<div className="mt-auto pt-3 sm:pt-4 flex flex-col gap-3">
					<div className="flex flex-col gap-1.5">
						<Skeleton className="h-6 sm:h-7 w-20" />
					</div>
					<Skeleton className="h-11 w-full rounded-full" />
				</div>
			</div>
		</div>
	);
}

export function ProductsSkeletonGrid({
	count = 8,
	columns = 4,
	className,
}: ProductsSkeletonGridProps) {
	return (
		<div
			className={cn(
				"grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6",
				columns === 4 && "lg:grid-cols-3 xl:grid-cols-4",
				columns === 3 && "lg:grid-cols-3",
				className,
			)}
		>
			{Array.from({ length: count }).map((_, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
				<ProductCardSkeleton key={i} />
			))}
		</div>
	);
}

interface BlogsSkeletonGridProps {
	count?: number;
	showFeatured?: boolean;
	className?: string;
}

function BlogCardSkeleton() {
	return (
		<motion.div
			variants={item}
			className="overflow-hidden transition-all duration-300 flex flex-col p-0 gap-0 shadow-sm border border-border bg-card rounded-xl h-full"
		>
			<Skeleton className="relative aspect-video w-full rounded-none shrink-0" />

			<div className="p-5 flex flex-col flex-1">
				<div className="flex items-center gap-2 mb-2 flex-wrap">
					<Skeleton className="h-5 w-16 rounded" />
					<Skeleton className="h-5 w-20 rounded" />
				</div>
				<Skeleton className="h-6 w-full" />
				<Skeleton className="h-6 w-2/3 mt-1" />

				<Skeleton className="h-4 w-full mt-4" />
				<Skeleton className="h-4 w-5/6 mt-1.5" />

				<div className="flex items-center gap-3 mt-auto pt-4 text-muted-foreground">
					<Skeleton className="h-3 w-20" />
					<Skeleton className="h-3 w-24" />
				</div>
			</div>
		</motion.div>
	);
}

export function BlogsSkeletonGrid({
	count = 6,
	showFeatured = true,
	className,
}: BlogsSkeletonGridProps) {
	return (
		<motion.div
			variants={container}
			initial="hidden"
			animate="show"
			className={cn("space-y-8", className)}
		>
			{showFeatured && (
				<motion.div variants={item}>
					<div className="bg-card rounded-xl overflow-hidden border border-border">
						<div className="grid md:grid-cols-2 gap-0 md:gap-6">
							<Skeleton className="relative aspect-video md:aspect-auto md:min-h-88 rounded-none" />
							<div className="p-6 md:p-8 flex flex-col justify-center">
								<div className="flex items-center gap-2 flex-wrap mb-3">
									<Skeleton className="h-5 w-20 rounded" />
									<Skeleton className="h-5 w-16 rounded" />
								</div>
								<Skeleton className="h-8 max-h-8 min-h-8 w-full" />
								<Skeleton className="h-8 max-h-8 min-h-8 w-3/4 mt-1" />

								<Skeleton className="h-4 w-full mt-4" />
								<Skeleton className="h-4 w-5/6 mt-1" />

								<div className="flex items-center gap-4 mt-4">
									<Skeleton className="h-4 w-24" />
									<Skeleton className="h-4 w-28" />
								</div>

								<Skeleton className="h-10 w-32 mt-6 rounded" />
							</div>
						</div>
					</div>
				</motion.div>
			)}

			<motion.div
				variants={container}
				className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8"
			>
				{Array.from({ length: count }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
					<BlogCardSkeleton key={i} />
				))}
			</motion.div>
		</motion.div>
	);
}

interface OrdersSkeletonProps {
	count?: number;
}

export function OrdersSkeleton({ count = 4 }: OrdersSkeletonProps) {
	return (
		<motion.div
			variants={container}
			initial="hidden"
			animate="show"
			className="space-y-4"
		>
			{Array.from({ length: count }).map((_, i) => (
				<motion.div
					// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
					key={i}
					variants={item}
					className="flex items-center justify-between p-4 rounded-xl border border-border bg-card"
				>
					<div className="flex items-center gap-4">
						<Skeleton className="h-10 w-10 rounded-lg" />
						<div className="space-y-2">
							<Skeleton className="h-4 w-24" />
							<Skeleton className="h-3 w-32" />
						</div>
					</div>
					<div className="text-right space-y-2">
						<Skeleton className="h-4 w-16 ml-auto" />
						<Skeleton className="h-3 w-20 ml-auto" />
					</div>
				</motion.div>
			))}
		</motion.div>
	);
}

interface AccountCardSkeletonProps {
	count?: number;
}

export function AccountCardSkeleton({ count = 3 }: AccountCardSkeletonProps) {
	return (
		<motion.div
			variants={container}
			initial="hidden"
			animate="show"
			className="grid grid-cols-1 md:grid-cols-3 gap-6"
		>
			{Array.from({ length: count }).map((_, i) => (
				<motion.div
					// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
					key={i}
					variants={item}
					className="rounded-xl border border-border bg-card p-6 space-y-4"
				>
					<div className="flex items-center justify-between">
						<Skeleton className="h-10 w-10 rounded-lg" />
						<Skeleton className="h-5 w-5 rounded" />
					</div>
					<div className="space-y-2 pt-2">
						<Skeleton className="h-5 w-24" />
						<Skeleton className="h-4 w-full" />
					</div>
					<div className="pt-2 space-y-1">
						<Skeleton className="h-5 w-20" />
						<Skeleton className="h-4 w-28" />
					</div>
				</motion.div>
			))}
		</motion.div>
	);
}

interface CartSkeletonProps {
	itemCount?: number;
}

export function CartSkeleton({ itemCount = 3 }: CartSkeletonProps) {
	return (
		<div className="grid gap-8 lg:grid-cols-3">
			<motion.div
				variants={container}
				initial="hidden"
				animate="show"
				className="lg:col-span-2 space-y-4"
			>
				{Array.from({ length: itemCount }).map((_, i) => (
					<motion.div
						// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
						key={i}
						variants={item}
						className="flex gap-4 rounded-xl bg-card p-4 border border-border"
					>
						<Skeleton className="h-20 w-20 shrink-0 rounded-lg" />
						<div className="flex flex-1 flex-col justify-between">
							<div className="space-y-2">
								<Skeleton className="h-4 w-3/4" />
								<Skeleton className="h-3 w-1/2" />
							</div>
							<div className="flex items-center justify-between mt-2">
								<div className="flex items-center gap-2">
									<Skeleton className="h-8 w-8 rounded" />
									<Skeleton className="h-4 w-8" />
									<Skeleton className="h-8 w-8 rounded" />
									<Skeleton className="h-8 w-8 rounded" />
								</div>
								<Skeleton className="h-5 w-16" />
							</div>
						</div>
					</motion.div>
				))}
			</motion.div>

			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ delay: 0.2 }}
				className="lg:col-span-1"
			>
				<div className="rounded-xl bg-card p-6 border border-border sticky top-24">
					<Skeleton className="h-6 w-32 mb-4" />
					<div className="space-y-3">
						<div className="flex justify-between">
							<Skeleton className="h-4 w-16" />
							<Skeleton className="h-4 w-20" />
						</div>
						<div className="flex justify-between">
							<Skeleton className="h-4 w-16" />
							<Skeleton className="h-4 w-28" />
						</div>
						<div className="border-t border-border pt-3 flex justify-between">
							<Skeleton className="h-5 w-24" />
							<Skeleton className="h-5 w-20" />
						</div>
					</div>
					<Skeleton className="h-12 w-full mt-6 rounded-lg" />
					<Skeleton className="h-3 w-48 mx-auto mt-4" />
				</div>
			</motion.div>
		</div>
	);
}

interface SearchSkeletonProps {
	count?: number;
}

export function SearchSkeleton({ count = 8 }: SearchSkeletonProps) {
	return (
		<motion.div
			variants={container}
			initial="hidden"
			animate="show"
			className="space-y-6"
		>
			<Skeleton className="h-4 w-48" />
			<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 md:gap-8">
				{Array.from({ length: count }).map((_, i) => (
					// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
					<ProductCardSkeleton key={i} />
				))}
			</div>
		</motion.div>
	);
}

export function ProductDetailsSkeleton() {
	return (
		<motion.div
			variants={container}
			initial="hidden"
			animate="show"
			className="space-y-16"
		>
			<div className="grid gap-12 lg:grid-cols-2">
				<motion.div variants={item} className="space-y-4">
					<Skeleton className="h-4 w-32" />
					<Skeleton className="aspect-square w-full rounded-2xl" />
					<div className="flex gap-2">
						{Array.from({ length: 4 }).map((_, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
							<Skeleton key={i} className="h-20 w-20 shrink-0 rounded-lg" />
						))}
					</div>
				</motion.div>

				<motion.div variants={item} className="space-y-6">
					<div className="flex items-center justify-between">
						<Skeleton className="h-4 w-24" />
						<Skeleton className="h-6 w-16 rounded" />
					</div>
					<Skeleton className="h-9 w-3/4" />
					<div className="flex items-baseline gap-3">
						<Skeleton className="h-8 w-24" />
						<Skeleton className="h-6 w-16" />
					</div>
					<div className="space-y-2">
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-full" />
						<Skeleton className="h-4 w-2/3" />
					</div>
					<div className="flex flex-wrap gap-2">
						{Array.from({ length: 4 }).map((_, i) => (
							// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
							<Skeleton key={i} className="h-7 w-16 rounded-full" />
						))}
					</div>
					<div className="space-y-2">
						<Skeleton className="h-4 w-16" />
						<div className="flex items-center gap-3">
							<Skeleton className="h-10 w-10 rounded-lg" />
							<Skeleton className="h-10 w-16 rounded-lg" />
							<Skeleton className="h-10 w-10 rounded-lg" />
						</div>
					</div>
					<Skeleton className="h-12 w-full rounded-lg" />
				</motion.div>
			</div>

			<div className="space-y-6">
				<Skeleton className="h-7 w-48" />
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
					{Array.from({ length: 4 }).map((_, i) => (
						// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
						<ProductCardSkeleton key={i} />
					))}
				</div>
			</div>
		</motion.div>
	);
}

export function GiftCardSkeleton() {
	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: 1 }}
			transition={{ duration: 0.3 }}
			className="container mx-auto px-4 py-12 md:py-24"
		>
			<ProductDetailsSkeleton />
		</motion.div>
	);
}

export function ContactFormSkeleton() {
	return (
		<motion.div
			variants={container}
			initial="hidden"
			animate="show"
			className="space-y-6"
		>
			{Array.from({ length: 4 }).map((_, i) => (
				// biome-ignore lint/suspicious/noArrayIndexKey: skeletons are static
				<motion.div key={i} variants={item} className="space-y-2">
					<Skeleton className="h-4 w-20" />
					<Skeleton className="h-10 w-full rounded-lg" />
				</motion.div>
			))}
			<motion.div variants={item} className="space-y-2">
				<Skeleton className="h-4 w-20" />
				<Skeleton className="h-32 w-full rounded-lg" />
			</motion.div>
			<motion.div variants={item}>
				<Skeleton className="h-12 w-full rounded-lg" />
			</motion.div>
		</motion.div>
	);
}
